/**
 * Auth context — wraps Supabase Auth for the entire app.
 * Provides: user, profile, loading, signUp, signIn, signOut, resetPassword,
 *           updateProfile, completeOnboarding
 */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);        // Supabase auth user
  const [profile, setProfile] = useState(null);   // profiles row
  const [attributes, setAttributes] = useState(null); // user_attributes row
  const [loading, setLoading] = useState(true);

  // ── Fetch profile from public.profiles ──
  const fetchProfile = useCallback(async (uid) => {
    if (!supabase || !uid) return null;
    const { data, error } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', uid)
      .single();
    if (error) { console.warn('[Auth] profile fetch:', error.message); return null; }
    return data;
  }, []);

  // ── Fetch user_attributes ──
  const fetchAttributes = useCallback(async (uid) => {
    if (!supabase || !uid) return null;
    const { data, error } = await supabase
      .from('user_attributes')
      .select('*')
      .eq('user_id', uid)
      .single();
    if (error && error.code !== 'PGRST116') {
      console.warn('[Auth] attributes fetch:', error.message);
    }
    return data || null;
  }, []);

  // ── Listen for auth state changes ──
  useEffect(() => {
    if (!supabase) { setLoading(false); return; }

    // NOTE: callback must NOT be async — Supabase v2 waits for the callback
    // to complete before resolving signInWithPassword, so awaiting inside it
    // causes a permanent hang. Use fire-and-forget .then() instead.
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const u = session?.user ?? null;
      setUser(u);
      setLoading(false);
      if (u) {
        fetchProfile(u.id).then(p => setProfile(p));
        fetchAttributes(u.id).then(a => setAttributes(a));
      } else {
        setProfile(null);
        setAttributes(null);
      }
    });

    // Initial session check (runs once on mount)
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      const u = session?.user ?? null;
      setUser(u);
      if (u) {
        const p = await fetchProfile(u.id);
        const a = await fetchAttributes(u.id);
        setProfile(p);
        setAttributes(a);
      }
      setLoading(false);
    });

    return () => subscription?.unsubscribe();
  }, [fetchProfile, fetchAttributes]);

  // ── Sign Up ──
  const signUp = async ({ email, password, fullName, company }) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { full_name: fullName, company } },
    });
    if (error) throw error;
    // Eagerly hydrate so ProtectedRoute doesn't bounce
    if (data.user) {
      setUser(data.user);
      setLoading(false);
      // Update profile row then hydrate (fire-and-forget)
      supabase.from('profiles')
        .update({ company, full_name: fullName })
        .eq('id', data.user.id)
        .then(() => fetchProfile(data.user.id).then(p => setProfile(p)));
      fetchAttributes(data.user.id).then(a => setAttributes(a));
    }
    return data;
  };

  // ── Sign In ──
  const signIn = async ({ email, password }) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    // Eagerly hydrate so ProtectedRoute doesn't see user=null before onAuthStateChange fires
    if (data.user) {
      setUser(data.user);
      setLoading(false);
      fetchProfile(data.user.id).then(p => setProfile(p));
      fetchAttributes(data.user.id).then(a => setAttributes(a));
    }

    console.log('[Auth] signIn successful, user:', data.user);
    return data;
  };

  // ── Sign Out ──
  const signOut = async () => {
    if (supabase) await supabase.auth.signOut();
    setUser(null);
    setProfile(null);
    setAttributes(null);
  };

  // ── Reset Password ──
  const resetPassword = async (email) => {
    if (!supabase) throw new Error('Supabase not configured');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/login?reset=1`,
    });
    if (error) throw error;
  };

  // ── Update Profile ──
  const updateProfile = async (updates) => {
    if (!supabase || !user) return;
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    setProfile(data);
    return data;
  };

  // ── Update User Attributes ──
  const updateAttributes = async (updates) => {
    if (!supabase || !user) return;
    const { data, error } = await supabase
      .from('user_attributes')
      .upsert({ user_id: user.id, ...updates }, { onConflict: 'user_id' })
      .select()
      .single();
    if (error) throw error;
    setAttributes(data);
    return data;
  };

  // ── Complete Onboarding ──
  const completeOnboarding = async ({ occupation, industry }) => {
    return updateProfile({ occupation, industry, onboarded: true });
  };

  // ── Helper: check roles ──
  const isAdmin = profile?.role === 'admin' || profile?.role === 'super_admin';
  const isSuperAdmin = profile?.role === 'super_admin';
  const hasPaidPackage = (pkgKey) => {
    // Will be checked against user_subscriptions — for now check profile
    return false; // default; subscription context handles this
  };

  return (
    <AuthContext.Provider value={{
      user,
      profile,
      attributes,
      loading,
      signUp,
      signIn,
      signOut,
      resetPassword,
      updateProfile,
      updateAttributes,
      completeOnboarding,
      isAdmin,
      isSuperAdmin,
      fetchProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
